import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParticipants, useRoomContext } from '@livekit/components-react';
import MeetingHeader from './MeetingHeader';
import ParticipantStage from './ParticipantStage';
import MeetingFooter from './MeetingFooter';
import CaptionsOverlay from './CaptionsOverlay';
import CopilotHostPanel from '../CopilotHostPanel';
import CustomChat from '../CustomChat';
import PeopleModal from '../modals/PeopleModal';
import { useCaptionsTranscript } from '../../hooks/useCaptionsTranscript';
import { useMeetingEndTimer } from '../../hooks/useMeetingEndTimer';
import { endMeeting } from '../../services/roomService';
import { isAgentParticipant } from '../../utils/participantMeta';
import '../../styles/meetingRoom.css';

/** Below this width, side panels overlay instead of shrinking the stage/footer. */
const NARROW_MEETING_WIDTH = 960;

/**
 * Getgeek custom meeting shell — replaces LiveKit VideoConference chrome.
 *
 * Layout mirrors AskAtlasLayout:
 *   [ header + stage + footer | side panel ]
 * Side panel is a full-height right column; only one of Sage/Chat/People at a time.
 */
export default function MeetingRoom({
  roomName,
  participantName,
  meetingTitle,
  isHost,
  onLeave,
  sageInvited,
  sageBusy,
  onInviteSage,
  sageSessionId,
  onSagePresent,
  onSageDismissed,
  showPeopleModal,
  setShowPeopleModal,
  setShowShareModal,
  isChatOpen,
  setIsChatOpen,
  isCopilotOpen,
  setIsCopilotOpen,
  sageContextPicker = null,
  scheduledEndTime = null,
}) {
  const room = useRoomContext();
  const meetingRef = useRef(null);
  const [captionsOn, setCaptionsOn] = useState(false);
  const [captionsHint, setCaptionsHint] = useState(false);
  const [layout, setLayout] = useState('grid');
  const [connectedAt] = useState(() => Date.now());
  const [narrow, setNarrow] = useState(false);
  const [overlayPanelContent, setOverlayPanelContent] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Ensure LiveKit publishes the display name (token name + runtime setName)
  useEffect(() => {
    const local = room?.localParticipant;
    const desired = String(participantName || '').trim();
    if (!local || !desired) return undefined;
    let cancelled = false;
    const syncName = async () => {
      try {
        if (local.name !== desired && typeof local.setName === 'function') {
          await local.setName(desired);
        }
      } catch (err) {
        if (!cancelled) {
          console.warn('Failed to set local participant name:', err?.message || err);
        }
      }
    };
    syncName();
    return () => {
      cancelled = true;
    };
  }, [room, participantName]);

  useEffect(() => {
    const el = meetingRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const update = () => {
      setNarrow(el.getBoundingClientRect().width < NARROW_MEETING_WIDTH);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const participants = useParticipants();
  const sagePresent = useMemo(
    () => (participants || []).some((p) => isAgentParticipant(p)),
    [participants]
  );

  const { transcriptLines, clearTranscript } = useCaptionsTranscript({
    enabled: true,
  });

  const handleMeetingTimedOut = useCallback(() => {
    if (roomName) {
      endMeeting(roomName, 'time').catch((err) => {
        console.warn('endMeeting (time) failed:', err.message);
      });
    }
    try {
      room?.disconnect();
    } catch {
      /* ignore */
    }
    onLeave?.();
  }, [room, onLeave, roomName]);

  const { warningVisible, secondsLeft } = useMeetingEndTimer({
    scheduledEndTime,
    enabled: Boolean(scheduledEndTime),
    onEnd: handleMeetingTimedOut,
  });

  useEffect(() => {
    if (!sagePresent) {
      setCaptionsOn(false);
      clearTranscript();
    }
  }, [sagePresent, clearTranscript]);

  const activeSidePanel = isCopilotOpen
    ? 'sage'
    : isChatOpen
      ? 'chat'
      : showPeopleModal
        ? 'people'
        : null;

  const openSidePanel = useCallback(
    (panel) => {
      setIsCopilotOpen(panel === 'sage');
      setIsChatOpen(panel === 'chat');
      setShowPeopleModal(panel === 'people');
    },
    [setIsCopilotOpen, setIsChatOpen, setShowPeopleModal]
  );

  const toggleSidePanel = useCallback(
    (panel) => {
      openSidePanel(activeSidePanel === panel ? null : panel);
    },
    [activeSidePanel, openSidePanel]
  );

  // App may open Sage externally (invite flows) — keep other panels closed.
  useEffect(() => {
    if (!isCopilotOpen) return;
    if (isChatOpen) setIsChatOpen(false);
    if (showPeopleModal) setShowPeopleModal(false);
  }, [isCopilotOpen, isChatOpen, showPeopleModal, setIsChatOpen, setShowPeopleModal]);

  const contextTitle = useMemo(() => {
    if (meetingTitle) return meetingTitle;
    if (participantName) return participantName;
    return 'Meetgeek interview';
  }, [meetingTitle, participantName]);

  const handleAskSage = useCallback(() => {
    if (!isHost) return;
    toggleSidePanel('sage');
  }, [isHost, toggleSidePanel]);

  const handleToggleCaptions = useCallback(() => {
    if (captionsOn) {
      setCaptionsOn(false);
      setCaptionsHint(false);
      return;
    }
    if (!sagePresent) {
      setCaptionsHint(true);
      window.setTimeout(() => setCaptionsHint(false), 3200);
      return;
    }
    setCaptionsHint(false);
    setCaptionsOn(true);
  }, [captionsOn, sagePresent]);

  const cycleLayout = useCallback(() => {
    setLayout((prev) => (prev === 'grid' ? 'focus' : 'grid'));
  }, []);

  const sidePanelOpen = Boolean(activeSidePanel);
  const overlayPanels = narrow;

  // Narrow drawers: mount off-screen first, then slide in (avoids open jerk).
  // On close, slide out before unmounting content.
  useEffect(() => {
    let raf1 = 0;
    let raf2 = 0;
    let timer = 0;

    if (activeSidePanel) {
      setOverlayPanelContent(activeSidePanel);
      if (overlayPanels) {
        setDrawerOpen(false);
        raf1 = window.requestAnimationFrame(() => {
          raf2 = window.requestAnimationFrame(() => {
            setDrawerOpen(true);
          });
        });
      } else {
        setDrawerOpen(true);
      }
      return () => {
        window.cancelAnimationFrame(raf1);
        window.cancelAnimationFrame(raf2);
      };
    }

    setDrawerOpen(false);
    if (!overlayPanels) {
      setOverlayPanelContent(null);
      return undefined;
    }
    timer = window.setTimeout(() => setOverlayPanelContent(null), 280);
    return () => window.clearTimeout(timer);
  }, [activeSidePanel, overlayPanels]);

  const panelToRender = overlayPanels
    ? overlayPanelContent
    : activeSidePanel;
  const railOpen = overlayPanels ? drawerOpen : sidePanelOpen;

  const warningMinutes =
    secondsLeft != null ? Math.max(1, Math.ceil(secondsLeft / 60)) : 5;

  return (
    <div
      ref={meetingRef}
      className={[
        'gg-meeting',
        sidePanelOpen ? 'gg-meeting--side-open' : '',
        overlayPanels ? 'gg-meeting--narrow' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {warningVisible && (
        <div className="gg-meeting-end-banner" role="status" aria-live="polite">
          Meeting ending in {warningMinutes} minute
          {warningMinutes === 1 ? '' : 's'}
        </div>
      )}

      <div className="gg-meeting__shell">
        <MeetingHeader contextTitle={contextTitle} connectedAt={connectedAt} />

        <div className="gg-meeting__stage-wrap">
          <ParticipantStage layout={layout} participantName={participantName} />
          <CaptionsOverlay
            lines={transcriptLines}
            visible={captionsOn}
            sagePresent={sagePresent}
            unavailableHint={captionsHint}
            isHost={isHost}
          />
        </div>

        <MeetingFooter
          roomName={roomName}
          isHost={isHost}
          onLeave={onLeave}
          onOpenPeople={() => toggleSidePanel('people')}
          onOpenShare={() => setShowShareModal(true)}
          onAskSage={handleAskSage}
          sageOpen={activeSidePanel === 'sage'}
          peopleOpen={activeSidePanel === 'people'}
          captionsOn={captionsOn}
          captionsAvailable={sagePresent}
          onToggleCaptions={handleToggleCaptions}
          layout={layout}
          onCycleLayout={cycleLayout}
          sageAvailable
          isChatOpen={activeSidePanel === 'chat'}
          onChatToggle={() => toggleSidePanel('chat')}
          forceCompact={overlayPanels}
        />
      </div>

      {overlayPanels && overlayPanelContent ? (
        <button
          type="button"
          className={`gg-side-rail-backdrop${drawerOpen ? ' is-open' : ''}`}
          aria-label="Close panel"
          onClick={() => openSidePanel(null)}
        />
      ) : null}

      <aside
        className={`gg-side-rail${railOpen ? ' gg-side-rail--open' : ''}`}
        aria-hidden={!railOpen}
      >
        <div className="gg-side-rail__inner">
          {panelToRender === 'sage' ? (
            <CopilotHostPanel
              isHost={isHost}
              isOpen
              onClose={() => openSidePanel(null)}
              roomName={roomName}
              sessionId={sageSessionId}
              onDismissed={onSageDismissed}
              onSagePresent={onSagePresent}
              docked
              sageInvited={sageInvited}
              sageBusy={sageBusy}
              onInviteSage={onInviteSage}
              contextPickerOpen={Boolean(sageContextPicker?.open)}
              contextPickerProps={sageContextPicker?.props || null}
            />
          ) : null}
          {/* Always mounted so LiveKit useChat history is not lost on close */}
          <CustomChat
            isOpen={panelToRender === 'chat'}
            onClose={() => openSidePanel(null)}
            docked
          />
          {panelToRender === 'people' ? (
            <PeopleModal
              isOpen
              onClose={() => openSidePanel(null)}
              participantName={participantName}
              localParticipantIsHost={isHost}
              docked
            />
          ) : null}
        </div>
      </aside>
    </div>
  );
}
